/**
 * Per-block width — the flat alternative to nesting.
 *
 * Instead of a columns container holding child blocks, a page stays a flat array and a block declares
 * how much of the row it takes. Consecutive non-full blocks flow up beside each other; a full block
 * breaks the row. The renderer does this with one wrapping flex row and a flex-basis per block — no
 * container, no child addressing, no one-level cap to police.
 *
 * ⚠️ ONE PLACE DECIDES, which is the whole reason this is a module rather than three ternaries. The
 * editor draws the width control from `effectiveWidth`, the renderer lays out from `effectiveWidth`,
 * and the validator accepts what `BLOCK_WIDTHS` contains. Three copies of "is this block full width"
 * is three chances for the preview to disagree with the published page.
 */

import type { BlockDef } from './prop-types.ts'
import type { Block, BlockWidth } from './types.ts'

export const BLOCK_WIDTHS: readonly BlockWidth[] = ['full', 'half', 'third'] as const

/** The width a block ACTUALLY renders at: `full` for a `fullOnly` block or an unset/unknown value,
 *  otherwise the declared width. */
export function effectiveWidth(block: Block, def: BlockDef | undefined): BlockWidth {
  if (def?.fullOnly) return 'full'
  return block.width === 'half' || block.width === 'third' ? block.width : 'full'
}

/** The CSS flex-basis for a width — the block's share of the row. */
export function widthBasis(w: BlockWidth): string {
  return w === 'half' ? '50%' : w === 'third' ? '33.333%' : '100%'
}

/**
 * The narrowest a non-full block may get before it stacks instead.
 *
 * ⚠️ IN `ch` RATHER THAN `px`, AND THAT IS AN ARABIC FIX. A third-width column at 280px holds a
 * readable English sentence and roughly two Arabic words per line, because Arabic script at the same
 * point size is wider and does not hyphenate. Sizing the stack threshold in characters means the
 * breakpoint follows the font the template actually chose, in the language the visitor is actually
 * reading.
 */
export function widthMin(w: BlockWidth): string {
  return w === 'full' ? '100%' : '28ch'
}
